/**
 * useKontaktListe — Infinite-Scroll-Hook für /api/kontakt-liste (Issue #641)
 *
 * Holt Kontakte seitenweise über Cursor-Paging vom Server.
 * Stellt loadMore() bereit für Infinite-Scroll-Trigger.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { KontaktListItem } from '../../../api/kontakt-liste';

export type { KontaktListItem };

export interface KontaktListeFilter {
  search?: string;
  kategorie?: string;
  firma_zuordnung?: string;
  favorit?: boolean;
  label?: string;
}

interface State {
  items: KontaktListItem[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
}

export function useKontaktKategorien(): string[] {
  const [kategorien, setKategorien] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('crm_kontakte')
          .select('kategorie')
          .not('kategorie', 'is', null)
          .limit(10000);
        if (!cancelled && data) {
          const unique = [
            ...new Set(
              data
                .map((k: { kategorie: string | null }) => k.kategorie)
                .filter((k): k is string => Boolean(k))
            ),
          ].sort();
          setKategorien(unique);
        }
      } catch {
        // Non-critical — category filter will just be empty
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return kategorien;
}

export function useKontaktListe(filter: KontaktListeFilter = {}) {
  const [state, setState] = useState<State>({
    items: [],
    total: 0,
    loading: true,
    loadingMore: false,
    error: null,
    hasMore: false,
  });

  // Keep cursor in a ref to avoid re-triggering the initial-load effect
  const cursorRef = useRef<string | null>(null);
  // Track whether initial load is done
  const initialLoadedRef = useRef(false);
  // Generation counter: increments on each filter change so stale loadMore responses can be discarded
  const generationRef = useRef(0);

  const buildUrl = useCallback(
    (cursor?: string | null) => {
      const params = new URLSearchParams();
      if (filter.search) params.set('search', filter.search);
      if (filter.kategorie) params.set('kategorie', filter.kategorie);
      if (filter.firma_zuordnung) params.set('firma_zuordnung', filter.firma_zuordnung);
      if (filter.favorit) params.set('favorit', 'true');
      if (filter.label) params.set('label', filter.label);
      if (cursor) params.set('cursor', cursor);
      const qs = params.toString();
      return `/api/kontakt-liste${qs ? `?${qs}` : ''}`;
    },
    [filter.search, filter.kategorie, filter.firma_zuordnung, filter.favorit, filter.label]
  );

  // Initial load + reload when filter changes
  useEffect(() => {
    generationRef.current += 1;
    cursorRef.current = null;
    initialLoadedRef.current = false;

    setState((prev) => ({ ...prev, loading: true, items: [], error: null, hasMore: false }));

    let cancelled = false;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        const headers: HeadersInit = {};
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }
        const r = await fetch(buildUrl(null), { headers });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const { items, nextCursor, total } = await r.json();
        if (cancelled) return;
        cursorRef.current = nextCursor;
        initialLoadedRef.current = true;
        setState({
          items,
          total,
          loading: false,
          loadingMore: false,
          error: null,
          hasMore: Boolean(nextCursor),
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[useKontaktListe] Fehler beim Laden:', err);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: 'Kontakte konnten nicht geladen werden',
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUrl]);

  const loadMore = useCallback(() => {
    if (!cursorRef.current || state.loadingMore || state.loading) return;

    const gen = generationRef.current;
    setState((prev) => ({ ...prev, loadingMore: true }));

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: HeadersInit = {};
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }
        const r = await fetch(buildUrl(cursorRef.current), { headers });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const { items, nextCursor, total } = await r.json();
        // Discard stale response if filter changed while fetch was in flight
        if (gen !== generationRef.current) return;
        cursorRef.current = nextCursor;
        setState((prev) => ({
          ...prev,
          items: [...prev.items, ...items],
          total,
          loadingMore: false,
          hasMore: Boolean(nextCursor),
        }));
      } catch (err) {
        if (gen !== generationRef.current) return;
        console.error('[useKontaktListe] Fehler beim Nachladen:', err);
        setState((prev) => ({
          ...prev,
          loadingMore: false,
          error: 'Weitere Kontakte konnten nicht geladen werden',
        }));
      }
    })();
  }, [buildUrl, state.loadingMore, state.loading]);

  return {
    items: state.items,
    total: state.total,
    loading: state.loading,
    loadingMore: state.loadingMore,
    error: state.error,
    hasMore: state.hasMore,
    loadMore,
  };
}
